/**
 * Decorative aurora background.
 *
 * The blobs used to move via SMIL `<animate attributeName="cx|cy">`, which
 * mutates SVG geometry on the main thread every frame and forces the Gaussian
 * blur to be re-evaluated over a fresh layout each time. They are now driven by
 * CSS `transform` keyframes (see snippets/aurora-bg.liquid) so the motion is a
 * compositor-friendly translate and can be paused declaratively.
 *
 * The rendered geometry is unchanged: each keyframe translate equals the delta
 * of the original cx/cy value list, with the same durations and linear timing.
 */
(function () {
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  // The snippet can be rendered more than once per page (the brand story page
  // uses it twice), so the filter needs a unique id per instance instead of a
  // shared `#aurora-blur` that would collide in the document.
  let instanceCount = 0;

  function markup(filterId) {
    return `
      <svg class="aurora-svg" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <filter id="${filterId}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="60" />
          </filter>
        </defs>
        <g filter="url(#${filterId})" opacity="0.5">
          <g class="aurora-blob__x aurora-blob__x--orange">
            <circle class="aurora-blob aurora-blob--orange" cx="300" cy="300" r="200" fill="#FF9D4D" />
          </g>
          <g class="aurora-blob__x aurora-blob__x--cyan">
            <circle class="aurora-blob aurora-blob--cyan" cx="500" cy="250" r="180" fill="#7AE7ED" />
          </g>
          <g class="aurora-blob__x aurora-blob__x--yellow">
            <circle class="aurora-blob aurora-blob--yellow" cx="400" cy="400" r="160" fill="#FFD561" />
          </g>
        </g>
      </svg>
    `;
  }

  class AuroraBackground extends HTMLElement {
    connectedCallback() {
      this.motionQuery = window.matchMedia(REDUCED_MOTION);

      if (!this.boundMotionChange) {
        this.boundMotionChange = this.onMotionChange.bind(this);
        if (typeof this.motionQuery.addEventListener === 'function') {
          this.motionQuery.addEventListener('change', this.boundMotionChange);
        } else if (typeof this.motionQuery.addListener === 'function') {
          this.motionQuery.addListener(this.boundMotionChange);
        }
      }

      this.render();
    }

    disconnectedCallback() {
      this.stopVisibilityTracking();
    }

    render() {
      // Under `prefers-reduced-motion: reduce` nothing is injected at all, so
      // there is no animation to run. The stylesheet also zeroes the keyframes
      // as a second line of defence for anything already on screen.
      if (this.motionQuery.matches || this.rendered) return;

      instanceCount += 1;
      this.innerHTML = markup(`aurora-blur-${instanceCount}`);
      this.rendered = true;
      this.startVisibilityTracking();
    }

    onMotionChange() {
      if (this.motionQuery.matches) {
        this.stopVisibilityTracking();
        this.innerHTML = '';
        this.rendered = false;
        return;
      }
      this.render();
    }

    /**
     * Aurora sections sit part-way down long pages. Without this the blurred
     * blobs keep animating (and keep forcing the filter to re-rasterise) while
     * scrolled far out of view.
     */
    startVisibilityTracking() {
      if (this.visibilityObserver || !('IntersectionObserver' in window)) return;

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle('aurora--paused', !entry.isIntersecting);
          });
        },
        { rootMargin: '128px 0px' }
      );

      this.visibilityObserver.observe(this);
    }

    stopVisibilityTracking() {
      if (!this.visibilityObserver) return;
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
  }

  if (!customElements.get('aurora-background')) {
    customElements.define('aurora-background', AuroraBackground);
  }
})();
